import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Link,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { api, ApiError } from '../api/client';
import type { RsvpQuestion, CalendarLinks as CalendarLinksType } from '../api/client';
import CustomQuestionsForm from '../components/rsvp/CustomQuestionsForm';
import CalendarLinksComponent from '../components/rsvp/CalendarLinks';

// ─── Types ───────────────────────────────────────────────────────────────────

interface RsvpEventData {
  event: {
    title: string;
    dateTime: string;
    endDateTime?: string;
    location?: string;
    locationUrl?: string;
    timezone: string;
    description?: string;
    status: string;
    template: { name: string };
    faqs: Array<{ id: string; question: string; answer: string; sortOrder: number }>;
  };
  guest: {
    name: string;
    plusOnesMax: number;
  };
  rsvp: {
    response: string;
    plusOnesCount: number;
    dietaryNotes?: string;
    message?: string;
    respondedAt: string;
  } | null;
  rsvpQuestions?: RsvpQuestion[];
  calendarLinks?: CalendarLinksType;
}

type RsvpResponse = 'YES' | 'NO' | 'MAYBE';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatEventDateTime(dateStr: string, timezone: string): string {
  try {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
      timeZoneName: 'short',
    }).format(date);
  } catch {
    // Fallback if timezone is invalid
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  }
}

function isEventPast(dateTimeStr: string): boolean {
  const eventTime = new Date(dateTimeStr).getTime();
  const gracePeriod = 24 * 60 * 60 * 1000;
  return Date.now() > eventTime + gracePeriod;
}

function isEventInactive(status: string, dateTimeStr: string): boolean {
  return status === 'CANCELLED' || status === 'COMPLETED' || isEventPast(dateTimeStr);
}

// ─── Loading Skeleton ────────────────────────────────────────────────────────

function RsvpSkeleton() {
  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
      </Stack>
    </Container>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function EventRsvpPage() {
  const { token } = useParams<{ token: string }>();

  // Data state
  const [data, setData] = useState<RsvpEventData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [httpStatus, setHttpStatus] = useState<number | null>(null);

  // RSVP form state
  const [selectedResponse, setSelectedResponse] = useState<RsvpResponse | null>(null);
  const [plusOnesCount, setPlusOnesCount] = useState(0);
  const [dietaryNotes, setDietaryNotes] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Custom question answers state
  const [questionAnswers, setQuestionAnswers] = useState<
    Array<{ questionId: string; answer: string }>
  >([]);

  // Post-submit state
  const [submitted, setSubmitted] = useState(false);
  const [submittedResponse, setSubmittedResponse] = useState<RsvpResponse | null>(null);
  const [calendarUrl, setCalendarUrl] = useState<string | null>(null);
  const [submittedCalendarLinks, setSubmittedCalendarLinks] = useState<CalendarLinksType | null>(null);

  // Viewing/changing existing RSVP
  const [changingResponse, setChangingResponse] = useState(false);

  // Set noindex meta tag
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  // Fetch event data
  const fetchData = useCallback(async () => {
    if (!token) {
      setError('Invalid RSVP link');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await api.rsvp.getEvent(token);
      setData(result);
    } catch (err) {
      if (err instanceof ApiError) {
        setHttpStatus(err.status);
        if (err.status === 404) {
          setError('This RSVP link is not valid. Please check the link in your invitation email.');
        } else if (err.status === 410) {
          setError(err.message);
        } else {
          setError('Something went wrong. Please try again later.');
        }
      } else {
        setError('Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle RSVP submission
  const handleSubmit = async () => {
    if (!token || !selectedResponse) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const result = await api.rsvp.submit({
        token,
        response: selectedResponse,
        plusOnesCount: selectedResponse === 'NO' ? 0 : plusOnesCount,
        dietaryNotes: dietaryNotes.trim() || undefined,
        message: message.trim() || undefined,
        answers: questionAnswers.length > 0 ? questionAnswers : undefined,
      });

      setSubmitted(true);
      setSubmittedResponse(selectedResponse);
      setCalendarUrl(result.calendarUrl ?? null);
      setSubmittedCalendarLinks(result.calendarLinks ?? null);
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else {
        setSubmitError('Failed to submit your RSVP. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle "change response" for already-submitted RSVP
  const handleChangeResponse = () => {
    setChangingResponse(true);
    setSubmitted(false);
    setSubmittedResponse(null);
    setCalendarUrl(null);
    setSubmittedCalendarLinks(null);
    setSelectedResponse(null);
    setPlusOnesCount(0);
    setDietaryNotes('');
    setMessage('');
    setQuestionAnswers([]);
  };

  // ─── Loading ─────────────────────────────────────────────────────────────

  if (loading) {
    return <RsvpSkeleton />;
  }

  // ─── Error ───────────────────────────────────────────────────────────────

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h5" gutterBottom fontWeight={600}>
              {httpStatus === 410 ? 'Event Unavailable' : 'Oops!'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {error}
            </Typography>
          </CardContent>
        </Card>
        <Footer />
      </Container>
    );
  }

  if (!data) return null;

  const { event, guest, rsvp } = data;
  const inactive = isEventInactive(event.status, event.dateTime);
  const hasExistingRsvp = rsvp && !changingResponse;

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {/* Event Header Card */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #5C6BC0 0%, #3949AB 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
          <Typography
            variant="overline"
            sx={{ opacity: 0.85, letterSpacing: 2 }}
          >
            You're invited
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
            {event.title}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
            {guest.name}
          </Typography>
        </CardContent>
      </Card>

      {/* Event Details Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            {/* Date/Time */}
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <EventIcon color="primary" sx={{ mt: 0.3 }} />
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  {formatEventDateTime(event.dateTime, event.timezone)}
                </Typography>
                {event.endDateTime && (
                  <Typography variant="body2" color="text.secondary">
                    Until {formatEventDateTime(event.endDateTime, event.timezone)}
                  </Typography>
                )}
              </Box>
            </Stack>

            {/* Location */}
            {event.location && (
              <Stack direction="row" alignItems="center" spacing={2}>
                <LocationOnIcon color="primary" />
                <Box>
                  {event.locationUrl ? (
                    <Link
                      href={event.locationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      color="inherit"
                    >
                      <Typography variant="body1">{event.location}</Typography>
                    </Link>
                  ) : (
                    <Typography variant="body1">{event.location}</Typography>
                  )}
                </Box>
              </Stack>
            )}

            {/* Timezone */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <AccessTimeIcon color="primary" />
              <Typography variant="body2" color="text.secondary">
                All times shown in {event.timezone}
              </Typography>
            </Stack>

            {/* Description */}
            {event.description && (
              <>
                <Divider />
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {event.description}
                </Typography>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* FAQs */}
      {event.faqs.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Frequently Asked Questions
            </Typography>
            {event.faqs.map((faq) => (
              <Accordion
                key={faq.id}
                disableGutters
                elevation={0}
                sx={{
                  '&:before': { display: 'none' },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px !important',
                  mb: 1,
                  '&:last-of-type': { mb: 0 },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={500}>{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Inactive event message */}
      {inactive && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              {event.status === 'CANCELLED'
                ? 'This event has been cancelled'
                : 'This event has ended'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              RSVPs are no longer being accepted for this event.
            </Typography>
            {rsvp && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Your response was: <strong>{rsvp.response}</strong>
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Post-submission success state */}
      {submitted && submittedResponse && !inactive && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
            {submittedResponse === 'YES' && (
              <>
                <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  You're all set! {'\uD83C\uDF89'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  We look forward to seeing you there.
                </Typography>
                {submittedCalendarLinks ? (
                  <CalendarLinksComponent links={submittedCalendarLinks} />
                ) : calendarUrl ? (
                  <Button
                    variant="contained"
                    size="large"
                    href={calendarUrl}
                    sx={{ mb: 2 }}
                  >
                    Add to Calendar
                  </Button>
                ) : null}
              </>
            )}
            {submittedResponse === 'NO' && (
              <>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Thanks for letting us know.
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  We'll miss you! If your plans change, you can always update your response.
                </Typography>
              </>
            )}
            {submittedResponse === 'MAYBE' && (
              <>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  We hope you can make it!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  You can update your response anytime.
                </Typography>
              </>
            )}
            <Box sx={{ mt: 3 }}>
              <Button variant="text" onClick={handleChangeResponse}>
                Change your response
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Existing RSVP (not changing, not just submitted) */}
      {hasExistingRsvp && !submitted && !inactive && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {rsvp.response === 'YES' && "You're going!"}
              {rsvp.response === 'NO' && "You've declined"}
              {rsvp.response === 'MAYBE' && "You've responded Maybe"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Response: <strong>{rsvp.response}</strong>
              {rsvp.plusOnesCount > 0 && ` (+${rsvp.plusOnesCount} guest${rsvp.plusOnesCount !== 1 ? 's' : ''})`}
            </Typography>
            {rsvp.response === 'YES' && data.calendarLinks && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <CalendarLinksComponent links={data.calendarLinks} />
              </Box>
            )}
            <Box sx={{ mt: 2 }}>
              <Button variant="text" onClick={handleChangeResponse}>
                Change your response
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* RSVP Form (no existing RSVP, or changing response) */}
      {!hasExistingRsvp && !submitted && !inactive && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Will you be attending?
            </Typography>

            {/* Response buttons */}
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              <Button
                variant={selectedResponse === 'YES' ? 'contained' : 'outlined'}
                color="primary"
                size="large"
                fullWidth
                onClick={() => setSelectedResponse('YES')}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  ...(selectedResponse === 'YES'
                    ? {}
                    : { borderWidth: 2, '&:hover': { borderWidth: 2 } }),
                }}
              >
                Yes, I'll be there!
              </Button>
              <Button
                variant={selectedResponse === 'MAYBE' ? 'contained' : 'outlined'}
                color="primary"
                size="large"
                fullWidth
                onClick={() => setSelectedResponse('MAYBE')}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  ...(selectedResponse === 'MAYBE'
                    ? {}
                    : { borderWidth: 2, '&:hover': { borderWidth: 2 } }),
                }}
              >
                Maybe
              </Button>
              <Button
                variant={selectedResponse === 'NO' ? 'contained' : 'text'}
                color={selectedResponse === 'NO' ? 'primary' : 'inherit'}
                size="large"
                fullWidth
                onClick={() => setSelectedResponse('NO')}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  color: selectedResponse === 'NO' ? undefined : 'text.secondary',
                }}
              >
                Sorry, can't make it
              </Button>
            </Stack>

            {/* Optional fields (shown for YES or MAYBE) */}
            {(selectedResponse === 'YES' || selectedResponse === 'MAYBE') && (
              <Stack spacing={2.5} sx={{ mb: 3 }}>
                <Divider />

                {/* Plus ones */}
                {guest.plusOnesMax > 0 && (
                  <TextField
                    label="Number of additional guests"
                    type="number"
                    value={plusOnesCount}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 0 && val <= guest.plusOnesMax) {
                        setPlusOnesCount(val);
                      }
                    }}
                    inputProps={{ min: 0, max: guest.plusOnesMax }}
                    helperText={`You may bring up to ${guest.plusOnesMax} additional guest${guest.plusOnesMax !== 1 ? 's' : ''}`}
                    fullWidth
                    size="small"
                  />
                )}

                {/* Dietary notes */}
                <TextField
                  label="Dietary restrictions or allergies"
                  value={dietaryNotes}
                  onChange={(e) => setDietaryNotes(e.target.value)}
                  placeholder="e.g., vegetarian, nut allergy..."
                  multiline
                  rows={2}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 500 }}
                />

                {/* Message to host */}
                <TextField
                  label="Message to the host (optional)"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Any message you'd like to share..."
                  multiline
                  rows={2}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 1000 }}
                />

                {/* Custom RSVP questions */}
                {data?.rsvpQuestions && data.rsvpQuestions.length > 0 && (
                  <>
                    <Divider />
                    <CustomQuestionsForm
                      questions={data.rsvpQuestions}
                      onChange={setQuestionAnswers}
                    />
                  </>
                )}
              </Stack>
            )}

            {/* Submit error */}
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}

            {/* Submit button */}
            {selectedResponse && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={handleSubmit}
                disabled={submitting}
                startIcon={
                  submitting ? <CircularProgress size={20} color="inherit" /> : undefined
                }
                sx={{ py: 1.5, fontSize: '1.05rem' }}
              >
                {submitting ? 'Submitting...' : 'Submit RSVP'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Footer />
    </Container>
  );
}

// ─── Footer ──────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <Box sx={{ textAlign: 'center', py: 3 }}>
      <Typography variant="caption" color="text.secondary">
        Powered by{' '}
        <Link href="/" color="inherit" underline="hover" fontWeight={500}>
          TimeToRSVP
        </Link>
      </Typography>
    </Box>
  );
}
